import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getConnectorPrice, numberToLetter } from "../utils/rateUtils";
import analyticsService from "../services/analyticsService";

const connectorIcons = {
  "Type 2": "/prise_type_2.svg",
  "Domestique UE": "/prise_type_e.svg",
  "Combo CCS EU": "/prise_CCS2.svg",
  Chademo: "/prise_CHAdeMO.svg",
  IEC_60309_2_three_32: "/type_K.svg",
};

const ConnectorsComponent = () => {
  const { t } = useTranslation();
  const [cpData, setCpData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const navigate = useNavigate();
  const { ref: reference } = useParams();
  const location = useLocation();
  const { evcpData, eligibilityData, partner } = location.state || {};

  useEffect(() => {
    if (!evcpData) {
      navigate("/error", {
        state: { errorText: t('noBorneData') },
      });
      return;
    }

    analyticsService.pushPageView('connector_selection', { station_id: evcpData?.reference });
    setCpData({ data: evcpData });
    
    // Auto-select the first available or preparing connector
    const availableConnector = evcpData?.connectors?.find(
      (c) => (c.status === "Available" || c.status === "Preparing") && c.type !== "DOMESTIC_E"
    );
    if (availableConnector) {
      setSelectedConnectorId(availableConnector.id);
    }

    setIsLoading(false);
  }, [evcpData, navigate]);

  const handleSelection = (connectorId) => {
    setSelectedConnectorId(connectorId);
  };

  const handleNext = () => {
    const selectedConnector = cpData?.data?.connectors?.find(
      (c) => c.id === selectedConnectorId
    );

    if (!selectedConnector) return;

    analyticsService.pushEvent('connector_selected', {
      connector_id: selectedConnector.reference,
      connector_type: selectedConnector.type,
      power_kw: Math.floor(selectedConnector.powerMax),
    });

    navigate("/payment", {
      state: { 
        connector: selectedConnector, 
        evcpData: cpData.data,
        reference,
        partner
      },
    });
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center space-y-8">
        <div className="flex space-x-1">
          {[...Array(6)].map((_, i) => (
            <span
              key={i}
              className={`w-2 h-2 rounded-full bg-indigo-600 animate-bounce`}
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
        <p className="text-black font-semibold text-lg">{t("loading")}</p>
      </div>
    );
  }

  // Filtrer les connecteurs pour masquer : type E
  const filteredConnectors = cpData?.data?.connectors?.filter(
    connector => connector.type !== "DOMESTIC_E"
  ) || [];

  return (
    <div className=" bg-white flex flex-col items-center justify-center p-4 pt-36">
      <div className="w-full max-w-2xl space-y-6 pb-75">
        <div className="rounded-xl border border-[#3f4a80]">
          <div className="details-header">{t("address")}</div>
          <div className="px-4 py-3 text-sm text-gray-800 space-y-3">
            {/* Address with line below */}
            <div className="w-full space-y-3">
              <div className="flex items-center gap-2">
                <img src="/Map_Point.svg" className="w-4 h-4" alt="location" />
                <span className="underline">{cpData?.data?.address}</span>
              </div>
              <div className="w-full border-gray-400"></div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <img src="/Plug_Circle.svg" alt="Lock icon" className="mt-1" />
          <div className="flex flex-col">
            {filteredConnectors.length > 1 ? (
              <h2 className="auth-title">{t("choose_connector")}</h2>
            ) : (
              <h2 className="auth-title">{t("your_connector")}</h2>
            )}
          </div>
        </div>

        <form className="space-y-4">
          {filteredConnectors.map((connector) => {
            const powerKW = Math.floor(connector.powerMax);
            const price = getConnectorPrice(connector, cpData?.data);
            const isAvailable = connector.status === "Available" || connector.status === "Preparing";
            const isPreparing = connector.status === "Preparing";

            return (
              <label
                key={connector.id}
                className={`flex items-center gap-4 p-4 rounded-xl shadow-md bg-white cursor-pointer transition hover:scale-[1.01] ${
                  isAvailable
                    ? "border-l-8 border-[#61D19D]"
                    : "border-l-8 border-[#C40505]"
                }
                ${
                  selectedConnectorId === connector.id
                    ? "ring-2 ring-blue-500"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="connector"
                  value={connector.id}
                  disabled={!isAvailable}
                  checked={selectedConnectorId === connector.id}
                  onChange={() =>
                    isAvailable && handleSelection(connector.id)
                  }
                  className="w-5 h-5 accent-[#5B3EFF]"
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-gray-800">
                    {t("connector", { number: numberToLetter(connector.number) })}
                  </span>
                  <span
                    className={`text-sm ${
                      isAvailable ? "text-[#37966A]" : "text-[#980404]"
                    }`}
                  >
                    {isPreparing ? 
                    `${t("available")} (${t("vehicle_plugged")})` : 
                      (isAvailable ? t("available") : t("unavailable"))
                    }
                  </span>
                </div>
                <div className="ml-auto">
                  <div className="text-lg font-semibold text-gray-700">
                    ⚡ {powerKW} kW
                  </div>
                  <div className="text-sm font-semibold text-gray-700">
                    ⚡ {price} €/kWh
                  </div>
                </div>
              </label>
            );
          })}
        </form>

        <button
          onClick={handleNext}
          disabled={!selectedConnectorId}
          className={`custom-button ${
            selectedConnectorId ? "" : "disabled-button"
          }`}
        >
          <span className="custom-button-text">{t("next")}</span>
        </button>
      </div>
    </div>
  );
};

export default ConnectorsComponent;
